import { useRef, useState } from 'react';

import {
  Button,
  AdvancedDialog,
  TimelineItem,
  Spinner,
} from '@yaradigitallabs/ahua-react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';

import { uploadBulkImportFile } from '../../store/tags.thunk';
import type { UploadBulkImportSuccessPayload } from '../../types/tags.types';
import { extractCsvContentAsJsonString } from '../../utils/bulk.import.helpers';
import bulkImportTemplateUrl from '../../../bulkImportsHistory/utils/bulkImportTemplate.csv?url';

import {
  BULK_IMPORT_MODAL_TITLE,
  bulkImportActionButtons,
  fileUploadResult,
  fileUploadStatusText,
} from './bulkImportConstants';
import BulkFileUploader from './BulkFileUploader';
import FileUploadStatus from './FileUploadStatus';
import type { User } from '@/features/session/types/session.types';

const BulkImportModal = ({ open, onOpenChange, onBulkImportUpload }) => {
  const dispatch = useAppDispatch();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [files, setFiles] = useState<File[]>([]);
  const user: User | null = useAppSelector(state => state.session.user);
  const controllerRef = useRef<AbortController | null>(null);

  const handleFiles = (selectedFile: File) => {
    const fileArray = Array.from([selectedFile]);
    setFiles(prev => [...prev, ...fileArray]);
  };

  const handleUpload = async (file: File) => {
    if (!file) return;

    const controller = new AbortController();
    controllerRef.current = controller;

    setUploading(true);

    try {
      const formData = await extractCsvContentAsJsonString(file, user || null);
      const result = (await dispatch(
        uploadBulkImportFile(formData)
      ).unwrap()) as UploadBulkImportSuccessPayload;

      if (result.hasValidationErrors) {
        onBulkImportUpload(fileUploadResult.UPLOAD_FAILED);
      } else {
        onBulkImportUpload(fileUploadResult.UPLOAD_SUCCESS);
      }
    } catch {
      onBulkImportUpload(fileUploadResult.UPLOAD_FAILED);
    } finally {
      onOpenChange(false);
      controllerRef.current = null;
      setUploading(false);
    }
  };

  const cancelUpload = () => {
    controllerRef.current?.abort();
    setFiles([] as File[]);
    setProgress(0);
    setUploading(false);
  };

  const handleFileChange = (file: File, progress) => {
    setProgress(progress);
    handleFiles(file);
  };

  const handleDownloadTemplate = () => {
    const anchor = document.createElement('a');

    anchor.href = bulkImportTemplateUrl;
    anchor.download = 'bulk-import-template.csv';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  return (
    <AdvancedDialog
      open={open}
      onOpenChange={onOpenChange}
      closeOnOverlayClick={false}
    >
      <AdvancedDialog.Content size="l">
        <AdvancedDialog.Start>
          <AdvancedDialog.Title>{BULK_IMPORT_MODAL_TITLE}</AdvancedDialog.Title>
          <AdvancedDialog.Close>
            <TimelineItem.IconButton
              colorConcept="brand"
              icon="Close"
              size="xs"
            />
          </AdvancedDialog.Close>
        </AdvancedDialog.Start>
        <AdvancedDialog.Middle>
          <BulkFileUploader
            file={files[0]}
            onHandleFileChange={handleFileChange}
          />
        </AdvancedDialog.Middle>
        {files[0] && (
          <>
            <div className="text-gray-500 font-bold ml-4 mt-4">
              {fileUploadStatusText.CAPTION}
            </div>
            <FileUploadStatus
              progress={progress}
              file={files[0]}
              cancelUpload={cancelUpload}
            />
          </>
        )}
        <AdvancedDialog.End>
          <div className="flex justify-between w-full">
            <div>
              <Button
                iconLeading="Download"
                colorConcept="brand"
                size="n"
                variant="ghost"
                onClick={handleDownloadTemplate}
              >
                {bulkImportActionButtons.DOWNLOAD_TEMPLATE_BUTTON_TEXT}
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                colorConcept="inverse"
                size="n"
                variant="primary"
                onClick={() => {
                  onOpenChange(false);
                  cancelUpload();
                }}
              >
                {bulkImportActionButtons.CANCEL_BUTTON_TEXT}
              </Button>
              <Button
                colorConcept="brand"
                size="n"
                variant="primary"
                disabled={progress < 100 || uploading}
                onClick={() => handleUpload(files[0])}
              >
                {uploading ? (
                  <Spinner
                    helperText="Brand Spinner"
                    loaderType="CIRCULAR"
                    strokeWidth="bold"
                    indicatorColor="White"
                    trackColor="White"
                    size="x8"
                    isInverse={true}
                  />
                ) : (
                  bulkImportActionButtons.UPLOAD_BUTTON_TEXT
                )}
              </Button>
            </div>
          </div>
        </AdvancedDialog.End>
      </AdvancedDialog.Content>
    </AdvancedDialog>
  );
};

export default BulkImportModal;
